import type { Currency, RiskHeatmapPoint } from '../../shared/types';
import { fmtPct } from '../../shared/format';
import { useMask } from '../lib/mask';
import './../styles/components/risk-distribution.css';

type Band = {
  key: 'low' | 'medium' | 'high';
  label: string;
  range: string;
  min: number;
  max: number;
  color: string;
};

const BANDS: Band[] = [
  { key: 'low', label: 'Low', range: '1–3', min: 1, max: 3, color: 'var(--risk-low)' },
  { key: 'medium', label: 'Medium', range: '4–6', min: 4, max: 6, color: 'var(--risk-med)' },
  { key: 'high', label: 'High', range: '7–10', min: 7, max: 10, color: 'var(--risk-high)' },
];

const TOP_N = 4;

export function RiskDistribution({
  points,
  ccy,
}: {
  points: RiskHeatmapPoint[];
  ccy: Currency;
}) {
  const { amount } = useMask();
  const total = points.reduce((s, p) => s + p.value_usd, 0);
  const bands = BANDS.map((b) => {
    const items = points
      .filter((p) => p.risk >= b.min && p.risk <= b.max)
      .sort((a, b) => b.value_usd - a.value_usd);
    const value = items.reduce((s, p) => s + p.value_usd, 0);
    return {
      ...b,
      items,
      value,
      share: total > 0 ? value / total : 0,
    };
  });

  return (
    <section class="panel risk-dist">
      <h3 class="panel-title">Risk Distribution</h3>
      <div class="risk-bar" role="img" aria-label="Portfolio risk distribution by band">
        {bands.map(
          (b) =>
            b.share > 0 && (
              <div
                class="risk-bar-seg"
                key={b.key}
                style={`flex: ${b.share}; background: ${b.color}`}
                title={`${b.label} ${b.range}: ${fmtPct(b.share)}`}
              />
            )
        )}
      </div>
      <ul class="risk-dist-rows">
        {bands.map((b) => (
          <li class="risk-dist-row" key={b.key}>
            <span class="risk-dist-swatch" style={`background: ${b.color}`} />
            <span class="risk-dist-label">
              {b.label} <span class="mono dim">{b.range}</span>
            </span>
            <span class="risk-dist-pct mono">{fmtPct(b.share)}</span>
            <span class="risk-dist-value mono">{amount(b.value, ccy)}</span>
            <span class="risk-dist-tops mono dim">
              {b.items.length === 0
                ? '—'
                : b.items
                    .slice(0, TOP_N)
                    .map((p) => p.symbol)
                    .join(', ') +
                  (b.items.length > TOP_N ? ` +${b.items.length - TOP_N}` : '')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
